import type { MouseEvent } from 'react';
import { useRouter } from 'next/router';
import { buildLoginHref } from '@lib/auth0/login-url';

const PRIMARY_MOUSE_BUTTON = 0;

// Crawlers read the href but never click, so they only ever see the plain
// login URL. /auth/login redirects to Auth0, whose robots.txt blocks
// everything, so a returnTo in the markup gave Google a distinct URL per page
// to index as "blocked by robots.txt".
const LOGIN_LINK_REL = 'nofollow';

// Modified clicks open a new tab or window from the href, so they get the
// plain login URL and land on the callback default.
const isPlainPrimaryClick = (event: MouseEvent<HTMLAnchorElement>) =>
  event.button === PRIMARY_MOUSE_BUTTON &&
  !event.metaKey &&
  !event.ctrlKey &&
  !event.shiftKey &&
  !event.altKey;

/**
 * Props for a login link on a page crawlers can reach. Unlike useLoginHref,
 * the `returnTo` is only added when a person clicks, so the markup holds one
 * login URL site-wide rather than one per page.
 */
export const useLoginLink = (
  returnTo?: string,
  parameters?: Record<string, string>
) => {
  const router = useRouter();

  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.defaultPrevented || !isPlainPrimaryClick(event)) return;

    event.preventDefault();
    window.location.assign(
      buildLoginHref(returnTo ?? router.asPath, parameters)
    );
  };

  return {
    href: buildLoginHref(undefined, parameters),
    rel: LOGIN_LINK_REL,
    onClick
  };
};

export default useLoginLink;
